import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  InputLabel,
  makeStyles,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { createSafeZone } from 'src/features/safeZoneSlice';
import { useDispatch } from 'react-redux';
import { SearchBox } from './SearchBox';

const initStateFromValue = {
  radius: '',
  name: '',
  address: ''
};

const DEFAULT_CENTER = [20.9963937, 105.8684172];
const DEFAULT_ZOOM = 13;

const customIcon = L.icon({
  iconUrl: '/static/iconSvg/pin_icon.svg',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

function MapClickHandler({ onClick }) {
  useMapEvents({
    click: e => {
      onClick(e);
    }
  });
  return null;
}

function ZoomControl({ classes }) {
  const map = useMapEvents({});

  const handleZoomIn = () => {
    map.zoomIn();
  };

  const handleZoomOut = () => {
    map.zoomOut();
  };

  return (
    <Box className={classes.zoomContainer}>
      <IconButton
        className={classes.zoomButton}
        onClick={handleZoomIn}
        size="small"
      >
        <AddIcon />
      </IconButton>
      <IconButton
        className={classes.zoomButton}
        onClick={handleZoomOut}
        size="small"
      >
        <RemoveIcon />
      </IconButton>
    </Box>
  );
}

function CreateSafeZone({ open, onClose }) {
  const classes = useStyles();
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const dispatch = useDispatch();

  const handleMapClick = event => {
    const { lat, lng } = event.latlng;
    const newPosition = [lat, lng];
    setMarkerPosition(newPosition);
    setMapCenter(newPosition);
  };

  const handleClose = () => {
    onClose();
    setMarkerPosition(null);
    setMapCenter(DEFAULT_CENTER);
  };

  const handleSubmit = data => {
    const safeZoneData = {
      name: data.name,
      address: data.address,
      radius: Number(data.radius),
      lat: markerPosition?.[0] || DEFAULT_CENTER[0],
      lng: markerPosition?.[1] || DEFAULT_CENTER[1]
    };

    dispatch(createSafeZone(safeZoneData));

    onClose();
    setMarkerPosition(null);
    setMapCenter(DEFAULT_CENTER);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { borderRadius: 8 }
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" className={classes.titleText}>
          Thêm mới vùng an toàn
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Formik
          initialValues={initStateFromValue}
          validationSchema={Yup.object().shape({
            name: Yup.string()
              .max(100)
              .required('Tên vùng an toàn không được để trống'),
            address: Yup.string()
              .max(200)
              .required('Địa chỉ không được để trống'),
            radius: Yup.number()
              .required('Bán kính không được để trống')
              .positive('Bán kính phải là số dương')
              .max(10000, 'Bán kính không được quá 10000m')
          })}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Map Section */}
                <Grid item xs={12}>
                  <Box className={classes.mapContainer}>
                    <SearchBox
                      onSearchResult={position => {
                        setMarkerPosition(position);
                        setMapCenter(position);
                      }}
                      classes={classes}
                    />
                    <MapContainer
                      center={mapCenter}
                      zoom={DEFAULT_ZOOM}
                      zoomControl={false}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.google.com/vt/lyrs=m&hl=vi&x={x}&y={y}&z={z}"
                        subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                        maxZoom={22}
                      />
                      <MapClickHandler onClick={handleMapClick} />
                      <ZoomControl classes={classes} />
                      {markerPosition && (
                        <Marker position={markerPosition} icon={customIcon}>
                          <Popup>
                            <Typography variant="body2">
                              Vị trí đã chọn
                            </Typography>
                          </Popup>
                        </Marker>
                      )}
                    </MapContainer>
                  </Box>
                  <Typography variant="caption" className={classes.mapHint}>
                    * Click vào bản đồ để chọn vị trí
                  </Typography>
                  {markerPosition && (
                    <Typography variant="body2" className={classes.coordinates}>
                      Tọa độ: {markerPosition[0].toFixed(7)},{' '}
                      {markerPosition[1].toFixed(7)}
                    </Typography>
                  )}
                </Grid>

                {/* Form Fields */}
                <Grid item xs={12} md={6}>
                  <InputLabel className={classes.label}>
                    Tên vùng an toàn <span style={{ color: 'red' }}>*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.name && errors.name)}
                    fullWidth
                    helperText={touched.name && errors.name}
                    size="small"
                    name="name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    variant="outlined"
                    placeholder="Nhập tên vùng an toàn"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InputLabel className={classes.label}>
                    Bán kính (m) <span style={{ color: 'red' }}>*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.radius && errors.radius)}
                    fullWidth
                    helperText={touched.radius && errors.radius}
                    type="number"
                    size="small"
                    name="radius"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.radius}
                    variant="outlined"
                    placeholder="Nhập bán kính (mét)"
                    className={classes.textField}
                    InputProps={{ inputProps: { min: 1, max: 10000 } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <InputLabel className={classes.label}>
                    Địa chỉ <span style={{ color: 'red' }}>*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.address && errors.address)}
                    fullWidth
                    helperText={touched.address && errors.address}
                    size="small"
                    name="address"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.address}
                    variant="outlined"
                    placeholder="Nhập địa chỉ"
                    className={classes.textField}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>

              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={handleClose}
                  className="btn-main btn-plain"
                  variant="contained"
                  color="primary"
                >
                  Hủy
                </Button>
                <Button
                  type="submit"
                  className="btn-main"
                  variant="contained"
                  color="primary"
                >
                  Thêm mới
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    padding: theme.spacing(2, 3)
  },
  titleText: {
    fontSize: '18px',
    fontWeight: 600
  },
  mapContainer: {
    width: '100%',
    height: '400px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: theme.spacing(1),
    position: 'relative'
  },
  searchBoxContainer: {
    position: 'absolute',
    top: '10px',
    left: '10px',
    zIndex: 1000,
    width: '300px'
  },
  zoomContainer: {
    position: 'absolute',
    right: '10px',
    top: '10px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  zoomButton: {
    backgroundColor: 'white',
    border: '1px solid #ddd',
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  searchInput: {
    backgroundColor: 'white',
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      height: 'auto',
      '& fieldset': {
        top: 0,
        borderColor: '#ddd'
      },
      '&:hover fieldset': {
        borderColor: '#d9042f'
      }
    },
  },
  searchInputField: {
    backgroundColor: 'white',
    height: '100%'
  },
  searchResults: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: '200px',
    overflowY: 'auto',
    marginTop: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    borderRadius: '4px',
    zIndex: 1001
  },
  searchResultItem: {
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    },
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  resultText: {
    fontSize: '13px',
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical'
  },
  mapHint: {
    color: '#666',
    fontStyle: 'italic',
    display: 'block',
    marginBottom: theme.spacing(1)
  },
  coordinates: {
    color: '#d9042f',
    fontWeight: 500,
    display: 'block',
    marginBottom: theme.spacing(2)
  },
  label: {
    fontSize: '16px',
    marginBottom: theme.spacing(2),
    color: '#333'
  },
  textField: {
    marginBottom: theme.spacing(1)
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    justifyContent: 'center'
  }
}));

export default React.memo(CreateSafeZone);
